import type { JSX } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchHealth } from "../../lib/api";
import { listTenants, type Tenant } from "../../lib/auth";
import { CardTitle } from "../Term";
import { kbAdminApi, type Audience, type KbSourceRow, type KbSourceType } from "../../lib/kbAdminApi";

/**
 * KB admin self-serve console section (FEATURE_KB_ADMIN). List sources, add one
 * (upload / Feishu links), trigger the guarded ingest, and set per-source access
 * (tenant × audience). Dark-merged: when the flag is off the section renders a
 * disabled notice (no leak). Design: docs/design/2026-07-13-admin-kb-self-serve-uiux.html.
 */

function VisibilityBadges({ tenantScope, audience }: { tenantScope: string; audience: Audience }): JSX.Element {
  const scopeLabel = tenantScope === "*" ? "所有租户" : tenantScope;
  return (
    <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
      <span className="cc-tag" style={{ background: "var(--bg-raised)", color: "var(--text-secondary)" }}>{scopeLabel}</span>
      <span className="cc-tag" style={{
        background: audience === "internal" ? "var(--color-warning-light)" : "var(--color-info-light)",
        color: audience === "internal" ? "var(--color-warning-dark)" : "var(--color-info-dark)",
      }}>{audience === "internal" ? "内部" : "公开"}</span>
    </span>
  );
}

/** Plain-Chinese label for a running phase (never leak the raw English enum). */
const PHASE_CN: Record<string, string> = { queued: "排队中", reading: "读取文档中", indexing: "建索引中", replacing: "更新中" };

function phaseLabel(row: KbSourceRow): { text: string; cls: "ok" | "warn" | "err" | "info" | "idle" } {
  const s = row.status;
  if (s.running) return { text: `同步中 · ${PHASE_CN[s.phase] ?? "处理中"}`, cls: "info" };
  if (s.phase === "needs_confirm") return { text: `需确认 · 内容会从 ${row.chunks} 段减到 ${s.result?.shrinkProspective ?? "?"} 段`, cls: "warn" };
  if (s.phase === "aborted") return { text: `已跳过 · ${s.result?.note ?? "没读到内容"},原内容保留`, cls: "warn" };
  if (s.phase === "error") return { text: `失败 · ${s.error ?? ""}`, cls: "err" };
  if (s.phase === "done") return { text: s.result?.note !== undefined ? `已同步 · ${s.result.note}` : "已同步", cls: "ok" };
  return { text: row.chunks > 0 ? "已同步" : "未同步", cls: row.chunks > 0 ? "ok" : "idle" };
}

export function KbSourcesSection(): JSX.Element {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [rows, setRows] = useState<KbSourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [accessFor, setAccessFor] = useState<KbSourceRow | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const reload = useCallback(async () => {
    try {
      const r = await kbAdminApi.list();
      setRows(r.sources);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchHealth().then((h) => {
      const on = h.kind === "ok" && h.health.features?.kbAdmin === true;
      setEnabled(on);
      if (on) void reload();
      else setLoading(false);
    });
  }, [reload]);

  // Poll while any source is running so the status/chunks update live.
  useEffect(() => {
    const anyRunning = rows.some((r) => r.status.running);
    if (!anyRunning) return;
    pollRef.current = window.setTimeout(() => void reload(), 1500);
    return () => { if (pollRef.current !== null) window.clearTimeout(pollRef.current); };
  }, [rows, reload]);

  const ingest = useCallback(async (source: string, force = false) => {
    try {
      await kbAdminApi.ingest(source, force);
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [reload]);

  const del = useCallback(async (source: string) => {
    try {
      await kbAdminApi.remove(source);
      setConfirmDel(null);
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [reload]);

  if (enabled === false) {
    return <div className="cc-card"><CardTitle>知识库</CardTitle><div className="cc-empty">知识库自助管理未启用（FEATURE_KB_ADMIN）。</div></div>;
  }
  if (loading || enabled === null) return <div className="cc-card"><div className="cc-skeleton" style={{ height: 240 }} /></div>;

  const totalChunks = rows.reduce((a, r) => a + Math.max(0, r.chunks), 0);
  const running = rows.filter((r) => r.status.running).length;

  return (
    <div className="cc-card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <CardTitle>知识库来源</CardTitle>
        <button className="login-btn" style={{ width: "auto", padding: "8px 14px" }} onClick={() => setShowAdd(true)}>+ 新增来源</button>
      </div>
      <p className="cc-page-desc" style={{ marginTop: 4 }}>在这里登记文档来源;登记后要点「同步」,小环才会真正把文档读进知识库。每条来源都能单独设定:哪些租户能用、以及是对客户公开还是仅内部员工可见。</p>

      <div style={{ display: "flex", gap: 16, margin: "12px 0" }}>
        <Stat k="来源" v={String(rows.length)} />
        <Stat k="知识片段" v={totalChunks.toLocaleString()} />
        <Stat k="同步中" v={String(running)} />
      </div>

      {err !== null && <div className="cc-empty" style={{ color: "var(--color-error)" }}>{err} <button className="cc-back" onClick={() => void reload()}>重试</button></div>}

      {rows.length === 0 ? <div className="cc-empty">还没有来源。点「新增来源」上传文档或贴 Feishu 链接。</div> : (
        <table className="cc-table">
          <thead><tr><th>来源名称</th><th>类型</th><th>可见范围</th><th>片段数</th><th>状态</th><th>操作</th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const st = phaseLabel(r);
              return (
                <tr key={r.source}>
                  <td><b>{r.source}</b></td>
                  <td style={{ color: "var(--text-tertiary)" }}>{r.type === "upload" ? "上传" : r.type === "feishu" ? "Feishu 抓取" : "代码 wiki"}</td>
                  <td><VisibilityBadges tenantScope={r.tenantScope} audience={r.audience} /></td>
                  <td>{r.chunks < 0 ? "—" : r.chunks}</td>
                  <td><span className={`cc-health cc-health-${st.cls === "ok" ? "ok" : st.cls === "err" ? "err" : "warn"}`}><span className="cc-dot" />{st.text}</span></td>
                  <td>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {r.status.phase === "needs_confirm" ? (
                        <button className="cc-action warn" onClick={() => void ingest(r.source, true)}>仍然同步(内容会减少)</button>
                      ) : (
                        <button className="cc-action" disabled={r.status.running} onClick={() => void ingest(r.source)}>{r.status.running ? "同步中…" : "同步"}</button>
                      )}
                      <button className="cc-action" onClick={() => setAccessFor(r)}>访问</button>
                      {confirmDel === r.source ? (
                        <>
                          <button className="cc-action warn" onClick={() => void del(r.source)}>确认删除?</button>
                          <button className="cc-action" onClick={() => setConfirmDel(null)}>取消</button>
                        </>
                      ) : (
                        <button className="cc-action" onClick={() => setConfirmDel(r.source)}>删除</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showAdd && <AddSourceModal existing={rows.map((r) => r.source)} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); void reload(); }} />}
      {accessFor !== null && <AccessModal row={accessFor} onClose={() => setAccessFor(null)} onSaved={() => { setAccessFor(null); void reload(); }} />}
    </div>
  );
}

function Stat({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", borderRadius: "var(--radius-lg)", padding: "10px 14px", minWidth: 120 }}>
      <div style={{ fontSize: "var(--text-caption)", color: "var(--text-tertiary)" }}>{k}</div>
      <div style={{ fontSize: "var(--text-h3)", fontWeight: 650, color: "var(--text-primary)" }}>{v}</div>
    </div>
  );
}

function AddSourceModal({ existing, onClose, onSaved }: { existing: string[]; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [type, setType] = useState<KbSourceType>("upload");
  const [source, setSource] = useState("");
  const [scopeKind, setScopeKind] = useState<"shared" | "tenant">("shared");
  const [tenant, setTenant] = useState("");
  const [audience, setAudience] = useState<Audience>("public");
  const [files, setFiles] = useState<{ name: string; content: string }[]>([]);
  const [urls, setUrls] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onFiles = async (list: FileList | null): Promise<void> => {
    if (list === null) return;
    const read = await Promise.all(Array.from(list).map(async (f) => ({ name: f.name, content: await f.text() })));
    setFiles(read);
  };

  // Client-side validity (mirrors the server SOURCE_RE), so an empty/invalid form
  // never reaches the server and the save button reflects readiness.
  const trimmedSource = source.trim();
  const sourceOk = /^[a-z0-9][a-z0-9-]{1,63}$/.test(trimmedSource);
  const inputOk = type === "upload" ? files.length > 0 : urls.split("\n").some((u) => u.trim().length > 0);
  const tenantOk = scopeKind === "shared" || tenant.trim().length > 0;
  const collides = sourceOk && existing.includes(trimmedSource); // re-registering an existing source = full replace
  const canSave = sourceOk && inputOk && tenantOk && !busy;

  const save = async (): Promise<void> => {
    // Friendly, field-specific messages instead of a raw server 400 JSON dump.
    if (!sourceOk) return setErr("请给这条来源起个英文名:小写字母/数字/连字符,2–64 位(上方灰字只是示例,需要自己填)");
    if (!inputOk) return setErr(type === "upload" ? "请选择至少一个 .md 文件" : "请至少贴一条飞书文档链接");
    if (!tenantOk) return setErr("指定租户时请填写租户 id");
    setBusy(true); setErr(null);
    try {
      await kbAdminApi.create({
        source: trimmedSource,
        type,
        tenantScope: scopeKind === "shared" ? "*" : tenant.trim(),
        audience,
        ...(type === "feishu" ? { feishuUrls: urls.split("\n").map((u) => u.trim()).filter(Boolean) } : {}),
        ...(type === "upload" ? { files } : {}),
      });
      onSaved();
    } catch (e) {
      // Strip a raw "HTTP 400 {json}" to something readable.
      const raw = e instanceof Error ? e.message : String(e);
      setErr(raw.includes("HTTP 409") ? "已存在同名 source(换一个标签,或直接在列表里同步它)" : raw.replace(/^HTTP \d+ .*/, "保存失败,请检查输入"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="login-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h3 style={{ margin: "0 0 4px" }}>新增来源</h3>
        <p className="cc-page-desc" style={{ margin: "0 0 12px" }}>这一步只是登记来源,不会立刻读取内容。保存后,在列表里点「同步」,小环才会把文档读进知识库。</p>

        <Seg value={type} onChange={setType} opts={[["upload", "上传文件"], ["feishu", "Feishu 链接"]]} />

        {type === "upload" ? (
          <label className="login-field"><span>markdown 文件(.md)</span>
            <input type="file" multiple accept=".md,text/markdown,text/plain" onChange={(e) => void onFiles(e.target.files)} />
            {files.length > 0 && <span style={{ fontSize: "var(--text-caption)", color: "var(--text-tertiary)" }}>已选 {files.length} 个:{files.map((f) => f.name).join(" · ")}</span>}
            <span style={{ fontSize: "var(--text-caption)", color: "var(--text-tertiary)" }}>这次上传的文件将作为该来源的<b>全部内容</b>;同步时未包含的旧文件会被移除(整组替换,不是追加)。</span>
          </label>
        ) : (
          <label className="login-field"><span>飞书文档链接(每行一条)</span>
            <textarea value={urls} onChange={(e) => setUrls(e.target.value)} rows={3} placeholder="https://xxx.feishu.cn/wiki/xxx" style={{ fontFamily: "var(--font-mono)", fontSize: 12 }} />
          </label>
        )}

        <label className="login-field"><span>来源名称(英文)<span style={{ color: "var(--color-error)" }}>*</span></span>
          <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="例如 loom-manual(需自己填)" />
          <span style={{ fontSize: "var(--text-caption)", color: source !== "" && !sourceOk ? "var(--color-error)" : "var(--text-tertiary)" }}>
            给这条来源起个英文名(小写字母/数字/连字符,2–64 位),用来区分不同来源。以后同名再同步,会用新内容覆盖这一条,不影响别的来源。
          </span>
          {collides && <span style={{ fontSize: "var(--text-caption)", color: "var(--color-warning-dark)" }}>注意:已存在同名来源「{trimmedSource}」——保存会<b>整组替换</b>它的现有内容。</span>}
        </label>

        <div className="login-field"><span>① 哪些租户能用</span>
          <Seg value={scopeKind} onChange={setScopeKind} opts={[["shared", "所有租户"], ["tenant", "指定租户"]]} />
          {scopeKind === "tenant" && <TenantSelect value={tenant} onChange={setTenant} />}
          <span style={{ fontSize: "var(--text-caption)", color: "var(--text-tertiary)" }}>
            「所有租户」= 每个客户都能用(如通用产品手册);「指定租户」= 只有选中的那个客户能用。
          </span>
        </div>
        <div className="login-field"><span>② 对谁可见</span>
          <Seg value={audience} onChange={setAudience} opts={[["public", "公开"], ["internal", "内部"]]} />
          <span style={{ fontSize: "var(--text-caption)", color: "var(--text-tertiary)" }}>
            「公开」= 上面范围内的所有人都能看到(含客户);「内部」= 只有内部权限的员工能看到。
          </span>
        </div>

        {err !== null && <div style={{ color: "var(--color-error)", fontSize: "var(--text-caption)", marginTop: 6 }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
          <button className="admin-cancel" onClick={onClose}>取消</button>
          <button className="login-btn" style={{ width: "auto", padding: "8px 14px" }} disabled={!canSave} onClick={() => void save()}>{busy ? "保存中…" : "保存来源"}</button>
        </div>
      </div>
    </div>
  );
}

function AccessModal({ row, onClose, onSaved }: { row: KbSourceRow; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [scopeKind, setScopeKind] = useState<"shared" | "tenant">(row.tenantScope === "*" ? "shared" : "tenant");
  const [tenant, setTenant] = useState(row.tenantScope === "*" ? "" : row.tenantScope);
  const [audience, setAudience] = useState<Audience>(row.audience);
  const [busy, setBusy] = useState(false);

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      await kbAdminApi.setAccess(row.source, scopeKind === "shared" ? "*" : tenant.trim(), audience);
      onSaved();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="login-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <h3 style={{ margin: "0 0 4px" }}>访问 · {row.source}</h3>
        <p className="cc-page-desc" style={{ margin: "0 0 12px" }}>改完可见范围后,记得回列表点「同步」,新范围才会对已入库的内容生效。</p>
        <div className="login-field"><span>① 哪些租户能用</span>
          <Seg value={scopeKind} onChange={setScopeKind} opts={[["shared", "所有租户"], ["tenant", "指定租户"]]} />
          {scopeKind === "tenant" && <TenantSelect value={tenant} onChange={setTenant} />}
        </div>
        <div className="login-field"><span>② 对谁可见</span>
          <Seg value={audience} onChange={setAudience} opts={[["public", "公开"], ["internal", "内部"]]} />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
          <button className="admin-cancel" onClick={onClose}>取消</button>
          <button className="login-btn" style={{ width: "auto", padding: "8px 14px" }} disabled={busy || (scopeKind === "tenant" && tenant.trim() === "")} onClick={() => void save()}>{busy ? "保存中…" : "保存访问"}</button>
        </div>
      </div>
    </div>
  );
}

/** A dropdown of the predefined tenants (#93 — never free-typed, so the
 *  audit-stable key can't be mistyped), mirroring the create-user dialog. Auto-
 *  selects the first tenant when none is chosen yet. */
function TenantSelect({ value, onChange }: { value: string; onChange: (v: string) => void }): JSX.Element {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  useEffect(() => {
    let alive = true;
    void listTenants().then((list) => {
      if (!alive) return;
      setTenants(list);
      if (value === "" && list[0] !== undefined) onChange(list[0].name);
    });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={{ marginTop: 6 }}>
      {value !== "" && tenants.every((t) => t.name !== value) && <option value={value}>{value}</option>}
      {tenants.length === 0 && value === "" && <option value="">（无可选租户,请先在「用户」里建租户）</option>}
      {tenants.map((t) => (
        <option key={t.name} value={t.name}>{t.displayName !== null ? `${t.displayName}(${t.name})` : t.name}</option>
      ))}
    </select>
  );
}

function Seg<T extends string>({ value, onChange, opts }: { value: T; onChange: (v: T) => void; opts: [T, string][] }): JSX.Element {
  // Track uses --bg-base (the off-white page bg), the active pill uses
  // --bg-surface (white) + a border + shadow — visibly distinct in BOTH themes.
  // (In light mode --bg-surface === --bg-raised === #fff, so the track must be
  // --bg-base or the selected state disappears; that was the earlier bug.)
  return (
    <div style={{ display: "flex", gap: 4, background: "var(--bg-base)", border: "1px solid var(--border-color)", borderRadius: "var(--radius-lg)", padding: 3 }}>
      {opts.map(([v, label]) => {
        const on = value === v;
        return (
          <button key={v} type="button" onClick={() => onChange(v)} aria-pressed={on} style={{
            flex: 1, padding: "7px 8px", borderRadius: "var(--radius-md)", cursor: "pointer",
            fontSize: "var(--text-body-sm)", fontWeight: on ? 650 : 500,
            background: on ? "var(--bg-surface)" : "transparent",
            color: on ? "var(--text-brand)" : "var(--text-secondary)",
            border: on ? "1px solid var(--border-color)" : "1px solid transparent",
            boxShadow: on ? "var(--shadow-sm)" : "none",
            transition: "background .12s, color .12s",
          }}>{label}</button>
        );
      })}
    </div>
  );
}
